"use client";

import { ExternalAccountID } from "@repo/db";
import * as Sentry from "@sentry/nextjs";
import { useEffect, useMemo, useRef, useTransition } from "react";
import { publishEventToSDK } from "@repo/sdk/publisher";
import {
  createPrismaAccount,
  getFeltRecords,
  type ProviderContext,
  updateAccountTermsVersion,
} from "src/app/home/actions";
import { OwletInitialContext } from "src/lib/definitions";
import useSWR from "swr";
import { INITIAL_CONTEXT_COOKIE_NAME } from "@repo/sdk";

/**
 * Combined device and provider context data
 */
// TODO update interface to assert terms agreement before accessing device content
interface FeltData {
  deviceContext: OwletInitialContext;
  providerContext: ProviderContext;
}

/**
 * Fetches the device context from window.portalInitialContext
 */
const fetchDeviceContext = (): OwletInitialContext | null => {
  if (typeof window === "undefined") return null;

  let initialContext = (window as any)
    .portalInitialContext as OwletInitialContext;

  // TODO: Remove cookie IC approach - fallback to document.cookie should be removed
  // fallback to document.cookie
  // Get all cookies
  const cookies = document.cookie.split(";").reduce(
    (acc, cookie) => {
      const [name, value] = cookie.trim().split("=");
      if (name && value) {
        acc[name] = decodeURIComponent(value);
      }
      return acc;
    },
    {} as Record<string, string>
  );

  // Try new cookie name first, then fallback to legacy name
  const initialContextCookie =
    cookies[INITIAL_CONTEXT_COOKIE_NAME] ||
    cookies[INITIAL_CONTEXT_COOKIE_NAME];
  if (initialContextCookie) {
    initialContext = JSON.parse(initialContextCookie);
  }
  return initialContext || null;
};

/**
 * Fetches provider context from Prisma
 */
const fetchProviderContext = async ([
  _key,
  externalAccountId,
  coveredPersonIds,
]: [string, ExternalAccountID, string[]]): Promise<ProviderContext> => {
  try {
    return await getFeltRecords(externalAccountId, coveredPersonIds);
  } catch (error) {
    console.error("Failed to fetch provider context:", error);
    Sentry.captureException(error, {
      tags: {
        context: "use-felt-data",
        action: "fetchProviderContext",
      },
      extra: {
        externalAccountId,
      },
    });
    throw error;
  }
};

/**
 * Hook for accessing combined device and provider data.
 * Uses SWR under the hood to independently poll both device context and provider context.
 *
 * Also provides backward-compatible terms management methods.
 *
 * @returns Object with data, loading state, and terms management methods
 *
 * @example
 * ```typescript
 * // New API
 * const { data, isLoading } = useFeltData();
 * if (isLoading) return <LoadingSpinner />;
 * if (!data?.providerContext.isRegistered) {
 *   // Redirect to registration
 * }
 * if (data?.providerContext.termsAgreed < requiredVersion) {
 *   // Show terms modal
 * }
 *
 * // Backward-compatible API
 * const { needsToAccept, acceptTerms, isAccepting } = useFeltData();
 * if (needsToAccept) return <TermsModal onAccept={acceptTerms} />;
 * ```
 */
export function useFeltData() {
  const [isAccepting, startTransition] = useTransition();

  // Poll for device context changes using SWR
  const { data: deviceContext, isLoading: isLoadingDevice } = useSWR(
    "portalInitialContext",
    fetchDeviceContext,
    {
      refreshInterval: 5000, // Check every 5 seconds for changes
      revalidateOnFocus: true, // Check when user returns to the tab
      revalidateOnReconnect: false,
      dedupingInterval: 2000, // Dedupe requests within 2 seconds to prevent excessive polling
    }
  );

  // Extract account info for provider context key
  const externalAccountId = deviceContext?.account?.id as
    | ExternalAccountID
    | undefined;
  const coveredPersonIds =
    deviceContext?.account?.coveredPersons?.map((p) => p.id) || [];

  // Poll for provider context changes using SWR (independently from device context)
  const {
    data: providerContext,
    isLoading: isLoadingProvider,
    mutate: mutateProvider,
  } = useSWR(
    externalAccountId
      ? ["providerContext", externalAccountId, coveredPersonIds]
      : null,
    fetchProviderContext,
    {
      refreshInterval: 5000, // Check every 5 seconds for changes
      revalidateOnFocus: true, // Check when user returns to the tab
      revalidateOnReconnect: false,
      dedupingInterval: 2000, // Dedupe requests within 2 seconds to prevent excessive polling
    }
  );

  // Combine data from both sources
  const feltData: FeltData | null = useMemo(() => {
    if (!deviceContext || !providerContext) return null;
    return {
      deviceContext,
      providerContext,
    };
  }, [deviceContext, providerContext]);

  const isLoading = isLoadingDevice || isLoadingProvider;

  // Backward-compatible terms methods
  const requiredVersion = parseInt(process.env.NEXT_PUBLIC_TNC_VERSION || "1");

  const needsToAccept =
    !isLoading &&
    feltData !== null &&
    feltData.providerContext.isRegistered &&
    (feltData.providerContext.termsAgreed === null ||
      feltData.providerContext.termsAgreed < requiredVersion);

  // Fire termsAcceptance event for returning users who have already accepted terms.
  // This notifies the native iOS SDK to share full account details.
  // Ref guard is needed because SWR polling (every 5s) produces new object references
  // for feltData, which re-triggers the useEffect — without this, the event fires repeatedly.
  const hasPublishedTermsEvent = useRef(false);
  useEffect(() => {
    if (hasPublishedTermsEvent.current) return;
    if (isLoading || !feltData) return;

    const { providerContext: ctx } = feltData;
    if (
      ctx.isRegistered &&
      ctx.termsAgreed !== null &&
      ctx.termsAgreed >= requiredVersion
    ) {
      publishEventToSDK({ topic: "termsAcceptance", data: "success" });
      hasPublishedTermsEvent.current = true;
    }
  }, [isLoading, feltData, requiredVersion]);

  const acceptTerms = () => {
    startTransition(async () => {
      try {
        const accountId =
          providerContext?.isRegistered && providerContext.accountId
            ? providerContext.accountId
            : null;

        if (!accountId) {
          throw new Error("No account ID available");
        }

        // Optimistically update the cache before making the API call
        await mutateProvider(
          async (currentData) => {
            // First, update the server using accountId
            const result = await updateAccountTermsVersion(accountId);

            // Return optimistically updated data
            if (currentData?.isRegistered) {
              return {
                ...currentData,
                termsAgreed: result.updatedVersion,
              };
            }
            return currentData;
          },
          {
            // Optimistic data to show immediately
            optimisticData: providerContext?.isRegistered
              ? {
                  ...providerContext,
                  termsAgreed: requiredVersion,
                }
              : providerContext,
            // Revalidate after the update completes
            revalidate: true,
            // Rollback on error if the update fails
            rollbackOnError: true,
          }
        );
      } catch (error) {
        console.error("Failed to accept terms:", error);
        throw error;
      }
    });
  };

  const registerAccount = async () => {
    if (!deviceContext?.account) {
      throw new Error("Device context not available");
    }

    try {
      const result = await createPrismaAccount({
        externalAccountId: deviceContext.account.id as ExternalAccountID,
        firstName: deviceContext.account.firstName || "Guardian",
        lastName: deviceContext.account.lastName || "Family",
        email: deviceContext.account.email,
        contactPhone: deviceContext.account.contactPhone,
      });

      // Trigger revalidation of provider context to reflect new account
      await mutateProvider();

      return result;
    } catch (error) {
      console.error("Failed to register account:", error);
      throw error;
    }
  };

  // TODO listen to the custom event from the SDK to update the IC.
  return {
    data: feltData,
    isLoading,
    // Backward-compatible terms API
    needsToAccept,
    acceptTerms,
    isAccepting,
    // Account registration
    registerAccount,
    // Expose mutation function for manual revalidation
    mutateProvider,
  };
}
